import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, Paper, Divider, FormControlLabel, Checkbox, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const QuestionForm = ({ question, testType, evaluates, onSave, onCancel }) => {
    const [questionText, setQuestionText] = useState(question?.question || '');
    const [category, setCategory] = useState(question?.category || '');
    const [correctAnswer, setCorrectAnswer] = useState(question?.correct_answer || '');
    const [isReverseScored, setIsReverseScored] = useState(question?.is_reverse_scored || false);
    const [options, setOptions] = useState(question?.options || [
        { label: 'A', text: '' },
        { label: 'B', text: '' },
        { label: 'C', text: '' },
        { label: 'D', text: '' }
    ]);

    useEffect(() => {
        if (question) {
            setQuestionText(question.question || '');
            setCategory(question.category || '');
            setCorrectAnswer(question.correct_answer || '');
            setIsReverseScored(question.is_reverse_scored || false);
            if (question.options && question.options.length > 0) {
                setOptions(question.options);
            }
        }
    }, [question]);

    const handleOptionChange = (index, text) => {
        const newOptions = [...options];
        newOptions[index].text = text;
        setOptions(newOptions);
    };

    const handleSave = () => {
        if (!questionText.trim()) {
            alert('Question text is required');
            return;
        }

        if (testType === 'soft_skills') {
            if (!category) {
                alert('Category is required for soft skills questions');
                return;
            }
            onSave({
                id: question?.id,
                question: questionText.trim(),
                question_type: 'soft_skills',
                category: category,
                is_reverse_scored: isReverseScored
            });
        } else {
            // technical
            if (options.some(opt => !opt.text.trim())) {
                alert('All options must be filled');
                return;
            }
            if (!correctAnswer) {
                alert('Correct answer is required');
                return;
            }
            onSave({
                id: question?.id,
                question: questionText.trim(),
                question_type: 'technical',
                correct_answer: correctAnswer,
                options: options
            });
        }
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    {question?.id ? 'Edit Question' : 'New Question'}
                </Typography>
                {onCancel && (
                    <IconButton onClick={onCancel} size="small">
                        <DeleteIcon />
                    </IconButton>
                )}
            </Box>
            <TextField
                label="Question Text"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                sx={{ mb: 2 }}
            />
            {testType === 'soft_skills' ? (
                <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={category}
                            label="Category"
                            onChange={(e) => setCategory(e.target.value)}
                        >
                            {evaluates.map((cat, index) => (
                                <MenuItem key={index} value={cat}>{cat}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Tooltip title="Pre otázky formulované negatívne (napr. 'Mávam problém...', 'Často mi uniknú...'). Vyššie skóre bude znamenať nižšiu kompetenciu.">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isReverseScored}
                                    onChange={(e) => setIsReverseScored(e.target.checked)}
                                    sx={{
                                        color: '#2563EB',
                                        '&.Mui-checked': {
                                            color: '#2563EB',
                                        },
                                    }}
                                />
                            }
                            label="Reverse Scored Question (Negatívna formulácia)"
                            sx={{ mb: 2 }}
                        />
                    </Tooltip>
                </>
            ) : (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Options:</Typography>
                    {options.map((option, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ minWidth: 30, fontWeight: 'bold' }}>{option.label}:</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                value={option.text}
                                onChange={(e) => handleOptionChange(index, e.target.value)}
                                sx={{ ml: 1 }}
                            />
                        </Box>
                    ))}
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Correct Answer</InputLabel>
                        <Select
                            value={correctAnswer}
                            label="Correct Answer"
                            onChange={(e) => setCorrectAnswer(e.target.value)}
                        >
                            <MenuItem value="A">A</MenuItem>
                            <MenuItem value="B">B</MenuItem>
                            <MenuItem value="C">C</MenuItem>
                            <MenuItem value="D">D</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                {onCancel && (
                    <Button onClick={onCancel} variant="outlined">
                        Cancel
                    </Button>
                )}
                <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' } }}>
                    Save
                </Button>
            </Box>
        </Paper>
    );
};

export default QuestionForm;

